import { useEffect, useState } from "react";
import {
  unlinkPlaidInstitution,
  removePlaidInstitution,
} from "../../../../../api/finances/plaidConnectivityAPI";
import ActionMenu from "../../../ActionMenu";
import ConfirmationModal from "../../ConfirmationModal";
import {
  formatAccountSubtype,
  formatCurrency,
  getCreditMetrics,
  getGroupLabel,
  getInstitutionAvailableCash,
  getInstitutionIconSrc,
  getUtilizationMessage,
} from "./helpers";
import ProgressBar from "../../../charts/progress/ProgressBar";
import { getCreditGuardianColor } from "../../../charts/utils/dynamicColorsBar";
import Tooltip from "../../../Tooltip";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";

function InstitutionItem({
  institution,
  defaultOpen,
  onInstitutionRemoved,
  onInstitutionUpdated,
  onAddAccounts,
  onRemoveInstitution,
  isAddingAccounts = false,
  isTestAccount = false,
}) {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const tooltips = content.institutions.tooltips;
  const itemCopy = content.institutions.item;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [isConfirmingAction, setIsConfirmingAction] = useState(false);
  const [actionError, setActionError] = useState("");

  const institutionName = institution.name;
  const GROUP_ORDER = {
    depository: 1,
    credit: 2,
    loan: 3,
  };

  const accountGroups = [...(institution.account_groups || [])].sort(
    (a, b) => (GROUP_ORDER[a.type] || 999) - (GROUP_ORDER[b.type] || 999),
  );

  const isDisconnected = institution.is_active === false;

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const openModal = (actionType) => {
    setModalAction(actionType);
    setActionError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isConfirmingAction) return;

    setIsModalOpen(false);
    setModalAction(null);
    setActionError("");
  };

  const handleConfirm = async () => {
    const institutionUuid = institution.uuid;

    if (!institutionUuid) {
      setActionError(itemCopy.missing_id_error);
      return;
    }

    setIsConfirmingAction(true);
    setActionError("");

    try {
      if (modalAction === "unlink") {
        await unlinkPlaidInstitution(institutionUuid);

        onInstitutionUpdated?.({
          ...institution,
          is_active: false,
          connection_status: "disconnected",
        });
      } else {
        if (onRemoveInstitution) {
          await onRemoveInstitution(institution);
        } else {
          await removePlaidInstitution(institutionUuid);
          onInstitutionRemoved?.(institution);
        }
      }

      setIsModalOpen(false);
      setModalAction(null);
    } catch (error) {
      setActionError(itemCopy.action_failed_error);
    } finally {
      setIsConfirmingAction(false);
    }
  };

  const config = itemCopy.actions[modalAction] || itemCopy.actions.unlink;

  const message = actionError
    ? `${actionError}\n\n${config.message}`
    : config.message;

  return (
    <div className={`institution-item-container ${isOpen ? "iic-open" : ""}`}>
      {/* HEADER */}
      <div className="iic-header">
        <div className="iic-header-left">
          <img
            className="urt-card-icon"
            src={getInstitutionIconSrc(institution)}
            alt={institutionName}
          />

          <p>{institutionName}</p>

          {isDisconnected && (
            <span className="institution-status-badge disconnected">
              {itemCopy.disconnected}
            </span>
          )}
        </div>

        <div className="insitution-metadata">
          <Tooltip
            title={tooltips.available_cash_global.title}
            content={tooltips.available_cash_global.description}
          >
            <p
              style={{
                fontWeight: "500",
                color: "#001d38",
                borderBottom: "1px dashed #001d38",
              }}
            >
              {formatCurrency(getInstitutionAvailableCash(institution))}
            </p>
          </Tooltip>

          <img
            src={`${process.env.PUBLIC_URL}/images/icons/arrow_down_blue_light.svg`}
            alt={itemCopy.toggle_alt}
            onClick={toggleOpen}
            style={{
              cursor: "pointer",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "0.2s",
            }}
          />
        </div>
      </div>

      {/* BODY */}
      {isOpen && (
        <div className="iic-body">
          <div className="iic-body-content">
            {accountGroups.length === 0 && <p>No accounts available.</p>}

            {accountGroups.map((group) => {
              const isCredit = group.type === "credit";

              return (
                <div
                  key={group.type}
                  style={{
                    marginBottom: "24px",
                  }}
                >
                  {isCredit ? (
                    <div
                      className="iic-group-header-credit"
                      style={{
                        paddingBottom: "20px",
                        borderBottom: "1px solid #edf2f7",
                      }}
                    >
                      <strong>{getGroupLabel(group.type)}</strong>

                      {(() => {
                        const credit = getCreditMetrics(group);
                        const globalMessage = getUtilizationMessage(
                          credit.percentUsed,
                          "global",
                        );

                        const hasNoLimit = !credit.limit || credit.limit === 0;

                        return hasNoLimit ? (
                          <Tooltip
                            title={tooltips.card_balance.title}
                            content={tooltips.card_balance.description}
                          >
                            <span
                              style={{
                                fontWeight: "500",
                                color: "#001d38",
                                borderBottom: "1px dashed #001d38",
                              }}
                            >
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(credit.spent)}
                            </span>
                          </Tooltip>
                        ) : (
                          <div className="credit-progress-bar-recap">
                            <ProgressBar
                              spent={credit.spent}
                              total={credit.limit}
                              getColor={getCreditGuardianColor}
                              showLabels={true}
                              variant="fraction"
                              tooltipConfig={{
                                spent: {
                                  title: tooltips.credit_used.title,
                                  content: tooltips.credit_used.description,
                                },
                                total: {
                                  title: tooltips.total_credit_limit.title,
                                  content:
                                    tooltips.total_credit_limit.description,
                                },
                                percentUsage: {
                                  percent: credit.percentUsed,
                                  title: globalMessage.title,
                                  content: globalMessage.content,
                                },
                              }}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        paddingBottom: "20px",
                        borderBottom: "1px solid #edf2f7",
                      }}
                    >
                      <strong>{getGroupLabel(group.type)}</strong>

                      {/* CASH */}
                      {group.type === "depository" &&
                        (group?.balances?.available ??
                          group?.balances?.current) != null && (
                          <Tooltip
                            title={tooltips.available_cash_institution.title}
                            content={
                              tooltips.available_cash_institution.description
                            }
                          >
                            <span
                              style={{
                                fontWeight: "500",
                                color: "#001d38",
                                borderBottom: "1px dashed #001d38",
                              }}
                            >
                              {formatCurrency(
                                group?.balances?.available ??
                                  group?.balances?.current,
                              )}
                            </span>
                          </Tooltip>
                        )}

                      {/* LOAN */}
                      {group.type === "loan" &&
                        (group?.balances?.current ??
                          group?.balances?.available) != null && (
                          <Tooltip
                            title={tooltips.aggregated_loan_debt.title}
                            content={tooltips.aggregated_loan_debt.description}
                          >
                            <span
                              style={{
                                fontWeight: "500",
                                color: "#001d38",
                                borderBottom: "1px dashed #001d38",
                              }}
                            >
                              {formatCurrency(
                                group?.balances?.current ??
                                  group?.balances?.available,
                              )}
                            </span>
                          </Tooltip>
                        )}
                    </div>
                  )}

                  {(group.accounts || []).map((account) => {
                    const isCreditCard = group.type === "credit";
                    const cardSpent = isCreditCard
                      ? Number(account?.balances?.current || 0)
                      : 0;
                    const cardLimit = isCreditCard
                      ? Number(account?.balances?.limit || 0)
                      : 0;
                    const cardUsagePercent =
                      cardLimit > 0
                        ? Math.round((cardSpent / cardLimit) * 100)
                        : 0;

                    const cardHasNoLimit =
                      isCreditCard && (cardLimit === 0 || !cardLimit);

                    return (
                      <div
                        className="iic-content-account-item"
                        key={account.uuid}
                      >
                        <div className="iic-content-account-recap">
                          <div className="iic-content-recap-name-mask">
                            <p>
                              <strong>
                                {account.name ||
                                  formatAccountSubtype(account.subtype)}
                              </strong>
                            </p>
                            <div>
                              {account.mask && (
                                <span className="iic-account-mask">
                                  **** {account.mask}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="iic-content-progress">
                            {cardHasNoLimit && (
                              <Tooltip
                                title={tooltips.card_balance.title}
                                content={
                                  tooltips.card_balance.no_limit_description
                                }
                              >
                                <span
                                  style={{
                                    fontWeight: "400",
                                    color: "#666b6f",
                                    fontSize: "14px",
                                    borderBottom: "1px dashed #666b6f",
                                  }}
                                >
                                  {formatCurrency(
                                    cardSpent,
                                    account?.balances?.iso_currency_code ||
                                      "USD",
                                  )}
                                </span>
                              </Tooltip>
                            )}

                            {isCreditCard &&
                              cardLimit > 0 &&
                              (() => {
                                const cardMessage = getUtilizationMessage(
                                  cardUsagePercent,
                                  "card",
                                );

                                return (
                                  <ProgressBar
                                    spent={cardSpent}
                                    total={cardLimit}
                                    getColor={getCreditGuardianColor}
                                    showLabels={true}
                                    variant="fraction"
                                    tooltipConfig={{
                                      spent: {
                                        title: tooltips.card_balance.title,
                                        content:
                                          tooltips.card_balance.description,
                                      },
                                      total: {
                                        title: tooltips.card_limit.title,
                                        content:
                                          tooltips.card_limit.description,
                                      },
                                      percentUsage: {
                                        percent: cardUsagePercent,
                                        title: cardMessage.title,
                                        content: cardMessage.content,
                                      },
                                    }}
                                  />
                                );
                              })()}

                            {!isCreditCard && (
                              <span
                                style={{
                                  fontWeight: "400",
                                  color: "#666b6f",
                                  fontSize: "14px",
                                }}
                              >
                                {formatCurrency(
                                  group.type === "depository"
                                    ? account.subtype === "cd"
                                      ? account?.balances?.current
                                      : account?.balances?.available !== null &&
                                        account?.balances?.available !==
                                          undefined
                                      ? account.balances.available
                                      : account?.balances?.current ?? 0
                                    : account?.balances?.current,
                                  account?.balances?.iso_currency_code || "USD",
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* ACTIONS */}
          <div className="iic-content-institution-action">
            {!isTestAccount && (
              <button
                className="cta-button secondary rounded"
                disabled={isAddingAccounts}
                onClick={() => onAddAccounts?.(institution)}
              >
                {isAddingAccounts
                  ? itemCopy.opening_plaid
                  : isDisconnected
                  ? itemCopy.reconnect
                  : itemCopy.add_account}
              </button>
            )}

            <ActionMenu
              triggerClass="iic-content-institution-more"
              actions={[
                ...(!isTestAccount && !isDisconnected
                  ? [
                      {
                        label: itemCopy.actions.unlink.label,
                        onClick: () => openModal("unlink"),
                      },
                    ]
                  : []),
                {
                  label: itemCopy.actions.remove.label,
                  onClick: () => openModal("remove"),
                  className: "text-danger",
                },
              ]}
            />
          </div>

          <ConfirmationModal
            isOpen={isModalOpen}
            onClose={closeModal}
            onConfirm={handleConfirm}
            title={config.title}
            message={message}
            confirmText={
              isConfirmingAction ? config.loadingText : config.confirmText
            }
            cancelText={itemCopy.cancel}
            isLoading={isConfirmingAction}
          />
        </div>
      )}
    </div>
  );
}

export default InstitutionItem;
